import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Download,
  Print,
  CheckCircle,
  Cancel,
  Warning,
  Person,
  Business,
  DateRange,
  Euro,
  LocationOn,
  Description,
  Assignment,
  Flight,
  Home,
  DirectionsCar,
  Info,
  Flag,
  DocumentScanner
} from '@mui/icons-material';
import api from '../services/api';

const PostingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [posting, setPosting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [a1Dialog, setA1Dialog] = useState(false);
  const [notificationDialog, setNotificationDialog] = useState(false);
  const [a1FormData, setA1FormData] = useState({
    a1_form_number: '',
    a1_issue_date: '',
    a1_expiry_date: ''
  });
  const [notificationData, setNotificationData] = useState({
    notification_date: '',
    notification_reference: ''
  });

  // EU Minimum Wages 2025
  const minimumWages = {
    'DE': { hourly: 12.00, monthly: 2080, country: 'Germany' },
    'NL': { hourly: 13.27, monthly: 2300, country: 'Netherlands' },
    'BE': { hourly: 11.50, monthly: 1990, country: 'Belgium' },
    'FR': { hourly: 11.65, monthly: 2020, country: 'France' },
    'LU': { hourly: 15.50, monthly: 2687, country: 'Luxembourg' },
    'AT': { hourly: 11.00, monthly: 1900, country: 'Austria' },
    'IE': { hourly: 12.70, monthly: 2200, country: 'Ireland' },
    'ES': { hourly: 7.82, monthly: 1350, country: 'Spain' },
    'IT': { hourly: 9.50, monthly: 1650, country: 'Italy' },
    'PL': { hourly: 5.85, monthly: 1015, country: 'Poland' },
    'DK': { hourly: 19.50, monthly: 3380, country: 'Denmark' },
    'SE': { hourly: 13.50, monthly: 2340, country: 'Sweden' },
    'FI': { hourly: 11.00, monthly: 1900, country: 'Finland' }
  };

  useEffect(() => {
    fetchPostingDetails();
  }, [id]);

  const fetchPostingDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/postings/${id}`);
      setPosting(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching posting:', error);
      setError('Failed to load posting details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'EUR') => {
    return new Intl.NumberFormat('en-EU', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getPostingStatus = () => {
    if (!posting) return { status: 'unknown', color: 'default' };
    
    const today = new Date();
    const startDate = new Date(posting.posting_start_date);
    const endDate = new Date(posting.posting_end_date);
    
    if (posting.status === 'cancelled') {
      return { status: 'Cancelled', color: 'default', icon: <Cancel /> };
    }
    
    if (posting.status === 'completed') {
      return { status: 'Completed', color: 'default', icon: <CheckCircle /> };
    }
    
    if (today < startDate) {
      const daysUntilStart = Math.ceil((startDate - today) / (1000 * 60 * 60 * 24));
      return { status: `Starting in ${daysUntilStart} days`, color: 'info', icon: <Info /> };
    }
    
    if (today > endDate) {
      return { status: 'Expired', color: 'error', icon: <Cancel /> };
    }
    
    const daysRemaining = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
    if (daysRemaining <= 30) {
      return { status: `Ending Soon (${daysRemaining} days)`, color: 'warning', icon: <Warning /> };
    }
    
    return { status: 'Active', color: 'success', icon: <CheckCircle /> };
  };

  const calculateDuration = () => {
    if (!posting) return '';
    const start = new Date(posting.posting_start_date);
    const end = new Date(posting.posting_end_date);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return `${days} days`;
  };

  const getA1Status = () => {
    if (!posting?.a1_form_number) {
      return { status: 'Missing', color: 'error', icon: <Warning /> };
    }
    
    if (posting.a1_expiry_date) {
      const expiryDate = new Date(posting.a1_expiry_date);
      const today = new Date();
      
      if (expiryDate < today) {
        return { status: 'Expired', color: 'error', icon: <Cancel /> };
      }
      
      const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 30) {
        return { status: `Expiring Soon (${daysUntilExpiry} days)`, color: 'warning', icon: <Warning /> };
      }
    }
    
    return { status: 'Valid', color: 'success', icon: <CheckCircle /> };
  };

  const checkWageCompliance = () => {
    if (!posting) return null;
    
    const countryWage = minimumWages[posting.host_country];
    if (!countryWage) return null;
    
    const actualWage = parseFloat(posting.actual_wage || 0);
    const minWage = posting.actual_wage_type === 'hourly' ? countryWage.hourly : countryWage.monthly;
    
    return {
      compliant: actualWage >= minWage,
      minWage,
      actualWage,
      difference: actualWage - minWage
    };
  };

  const handleA1Submit = async () => {
    try {
      await api.post(`/postings/${id}/a1`, a1FormData);
      await fetchPostingDetails();
      setA1Dialog(false);
      setA1FormData({ a1_form_number: '', a1_issue_date: '', a1_expiry_date: '' });
    } catch (error) {
      console.error('Error updating A1:', error);
      setError('Failed to update A1 information');
    }
  };

  const handleNotificationSubmit = async () => {
    try {
      await api.post(`/postings/${id}/notification`, {
        notification_sent: true,
        ...notificationData
      });
      await fetchPostingDetails();
      setNotificationDialog(false);
      setNotificationData({ notification_date: '', notification_reference: '' });
    } catch (error) {
      console.error('Error updating notification:', error);
      setError('Failed to update notification');
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (window.confirm(`Are you sure you want to mark this posting as ${newStatus}?`)) {
      try {
        await api.put(`/postings/${id}`, { status: newStatus });
        await fetchPostingDetails();
      } catch (error) {
        console.error('Error updating status:', error);
        setError('Failed to update status');
      }
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography>Loading posting details...</Typography>
      </Container>
    );
  }

  if (error || !posting) {
    return (
      <Container>
        <Alert severity="error">{error || 'Posting not found'}</Alert>
        <Button onClick={() => navigate('/admin/postings')} sx={{ mt: 2 }}>
          Back to Postings
        </Button>
      </Container>
    );
  }

  const postingStatus = getPostingStatus();
  const a1Status = getA1Status();
  const wageCompliance = checkWageCompliance();
  const hostCountry = minimumWages[posting.host_country];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/admin/postings')}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h4">
              Posting: {posting.posting_number || `POST-${posting.id}`}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              {postingStatus.icon}
              <Chip 
                label={postingStatus.status}
                color={postingStatus.color}
                size="small"
              />
              <Chip 
                label={posting.posting_type}
                size="small"
                variant="outlined"
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <img 
                  src={`https://flagcdn.com/24x18/${posting.host_country?.toLowerCase()}.png`}
                  alt={posting.host_country}
                  style={{ width: 24, height: 18 }}
                />
                <Typography variant="body2">
                  {hostCountry?.country || posting.host_country}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            disabled
            title="Report generation coming soon"
          >
            Download Report
          </Button>
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={() => navigate(`/admin/postings/edit/${id}`)}
          >
            Edit Posting
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Employee & Contract Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Person color="primary" />
                <Typography variant="h6">Employee Information</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Name
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                    }}
                    onClick={() => navigate(`/admin/employees/${posting.employee_id}`)}
                  >
                    {posting.employee_first_name} {posting.employee_last_name}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Personal Code
                  </Typography>
                  <Typography variant="body1">
                    {posting.employee_personal_code}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Contract
                  </Typography>
                  <Typography 
                    variant="body1"
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { color: 'primary.main', textDecoration: 'underline' }
                    }}
                    onClick={() => navigate(`/admin/contracts/${posting.contract_id}`)}
                  >
                    {posting.contract_number}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Position
                  </Typography>
                  <Typography variant="body1">
                    {posting.contract_position}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Posting Duration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DateRange color="primary" />
                <Typography variant="h6">Posting Duration</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Start Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(posting.posting_start_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    End Date
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(posting.posting_end_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Duration
                  </Typography>
                  <Typography variant="body1">
                    {calculateDuration()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Tax Residency Risk
                  </Typography>
                  <Typography variant="body1">
                    {parseInt(calculateDuration()) > 183 ? (
                      <Chip label="High (>183 days)" color="error" size="small" />
                    ) : (
                      <Chip label="Low (<183 days)" color="success" size="small" />
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Host Company */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Business color="primary" />
                <Typography variant="h6">Host Company</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Company Name
                  </Typography>
                  <Typography variant="body1">
                    {posting.host_company_name}
                  </Typography>
                </Grid>
                {posting.host_company_code && (
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Company Code/VAT
                    </Typography>
                    <Typography variant="body1">
                      {posting.host_company_code}
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Address
                  </Typography>
                  <Typography variant="body1">
                    {posting.host_company_address}
                  </Typography>
                </Grid>
                {posting.host_company_contact_person && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Contact Person
                      </Typography>
                      <Typography variant="body1">
                        {posting.host_company_contact_person}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Contact Email
                      </Typography>
                      <Typography variant="body1">
                        {posting.host_company_contact_email || 'N/A'}
                      </Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Wage Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Euro color="primary" />
                <Typography variant="h6">Wage & Compliance</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Actual Wage
                  </Typography>
                  <Typography variant="h5" color={wageCompliance && !wageCompliance.compliant ? 'error' : 'primary'}>
                    {formatCurrency(posting.actual_wage, posting.actual_wage_currency)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {posting.actual_wage_type}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Minimum Wage
                  </Typography>
                  <Typography variant="h5">
                    {formatCurrency(
                      posting.actual_wage_type === 'hourly' 
                        ? hostCountry?.hourly 
                        : hostCountry?.monthly,
                      'EUR'
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {posting.actual_wage_type} in {hostCountry?.country}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  {wageCompliance && (
                    <Alert 
                      severity={wageCompliance.compliant ? 'success' : 'error'}
                      icon={wageCompliance.compliant ? <CheckCircle /> : <Warning />}
                    >
                      {wageCompliance.compliant 
                        ? `Wage compliant: €${wageCompliance.difference.toFixed(2)} above minimum`
                        : `Wage non-compliant: €${Math.abs(wageCompliance.difference).toFixed(2)} below minimum`
                      }
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* A1 Form Status */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Assignment color="primary" />
                <Typography variant="h6">A1 Form (Social Security)</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {posting.a1_form_number ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {a1Status.icon}
                      <Typography variant="body1">
                        Status: {a1Status.status}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Form Number
                    </Typography>
                    <Typography variant="body1">
                      {posting.a1_form_number}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Issue Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(posting.a1_issue_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Expiry Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(posting.a1_expiry_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Issuing Authority
                    </Typography>
                    <Typography variant="body1">
                      SODRA Lithuania
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    A1 form not registered. This is required for social security coverage.
                  </Alert>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setA1Dialog(true)}
                  >
                    Add A1 Information
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Host Country Notification */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Flag color="primary" />
                <Typography variant="h6">Host Country Notification</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {posting.notification_sent ? (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CheckCircle color="success" />
                      <Typography variant="body1">
                        Notification Submitted
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Submission Date
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(posting.notification_date)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Reference Number
                    </Typography>
                    <Typography variant="body1">
                      {posting.notification_reference || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <Box>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    Host country notification pending. Required before posting starts.
                  </Alert>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={() => setNotificationDialog(true)}
                  >
                    Mark as Notified
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Services */}
        {(posting.accommodation_provided || posting.transport_provided) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Additional Services
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  {posting.accommodation_provided && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Home color="action" />
                        <Box>
                          <Typography variant="subtitle2">
                            Accommodation Provided
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {posting.accommodation_address || 'Address to be confirmed'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {posting.transport_provided && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <DirectionsCar color="action" />
                        <Box>
                          <Typography variant="subtitle2">
                            Transport Provided
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {posting.transport_details || 'Details to be confirmed'}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Compliance Checklist */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Compliance Checklist
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <List>
                <ListItem>
                  <ListItemIcon>
                    {posting.a1_form_number ? <CheckCircle color="success" /> : <Cancel color="error" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="A1 Form (Social Security)"
                    secondary={posting.a1_form_number ? `Valid until ${formatDate(posting.a1_expiry_date)}` : 'Required for posting'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {posting.notification_sent ? <CheckCircle color="success" /> : <Warning color="warning" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Host Country Notification"
                    secondary={posting.notification_sent ? 'Submitted' : 'Pending submission'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {wageCompliance?.compliant ? <CheckCircle color="success" /> : <Warning color="error" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Minimum Wage Compliance"
                    secondary={wageCompliance?.compliant ? 'Meets requirements' : 'Below minimum wage'}
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    {parseInt(calculateDuration()) <= 183 ? <CheckCircle color="success" /> : <Warning color="warning" />}
                  </ListItemIcon>
                  <ListItemText 
                    primary="Tax Residency (183-day rule)"
                    secondary={parseInt(calculateDuration()) <= 183 ? 'Within safe period' : 'Risk of tax residency change'}
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Notes */}
        {posting.notes && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Description color="primary" />
                  <Typography variant="h6">Notes</Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body1" style={{ whiteSpace: 'pre-wrap' }}>
                  {posting.notes}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Posting Actions
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {posting.status === 'pending' && (
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleStatusChange('active')}
                  >
                    Activate Posting
                  </Button>
                )}
                {posting.status === 'active' && (
                  <Button
                    variant="contained"
                    color="info"
                    onClick={() => handleStatusChange('completed')}
                  >
                    Mark as Completed
                  </Button>
                )}
                {(posting.status === 'pending' || posting.status === 'active') && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleStatusChange('cancelled')}
                  >
                    Cancel Posting
                  </Button>
                )}
                <Button
                  variant="outlined"
                  onClick={() => navigate(`/admin/postings/edit/${id}`)}
                >
                  Edit Details
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* A1 Dialog */}
      <Dialog open={a1Dialog} onClose={() => setA1Dialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add A1 Form Information</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="A1 Form Number"
                value={a1FormData.a1_form_number}
                onChange={(e) => setA1FormData({ ...a1FormData, a1_form_number: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Issue Date"
                type="date"
                value={a1FormData.a1_issue_date}
                onChange={(e) => setA1FormData({ ...a1FormData, a1_issue_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Expiry Date"
                type="date"
                value={a1FormData.a1_expiry_date}
                onChange={(e) => setA1FormData({ ...a1FormData, a1_expiry_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setA1Dialog(false)}>Cancel</Button>
          <Button onClick={handleA1Submit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Notification Dialog */}
      <Dialog open={notificationDialog} onClose={() => setNotificationDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Host Country Notification as Sent</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notification Date"
                type="date"
                value={notificationData.notification_date}
                onChange={(e) => setNotificationData({ ...notificationData, notification_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reference Number (Optional)"
                value={notificationData.notification_reference}
                onChange={(e) => setNotificationData({ ...notificationData, notification_reference: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialog(false)}>Cancel</Button>
          <Button onClick={handleNotificationSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PostingDetail;
